import { useLayoutEffect, useState } from 'react'
import { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import useCallbackReminders, { CallbackReminder, CallbackReminderId } from '@/hoc/hooks/useCallbackReminders'
import { formatCallbackTime, isCallbackExpired, minutesUntilCallback } from './callbackReminderDates'

export const CALLBACK_REMINDER_CLOCK_MS = 10000

export interface CallbackReminderCardView {
  id: CallbackReminderId
  title: string
  subtitle: string
  customer: string
  phone: string
  isLate: boolean
  hasContact: boolean
  isDismissing: boolean
  closeLabel: string
  openContactLabel: string
  onClose: () => void
  onOpenContact: () => void
}

interface UseCallbackReminderPopupReturn {
  cards: CallbackReminderCardView[]
  stackLabel: string
}

const useNow = (enabled: boolean): number => {
  const [now, setNow] = useState(() => Date.now())

  useLayoutEffect(() => {
    if (!enabled) return undefined

    const refresh = () => setNow(Date.now())
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') refresh()
    }

    refresh()
    const interval = setInterval(refresh, CALLBACK_REMINDER_CLOCK_MS)
    document.addEventListener('visibilitychange', refreshWhenVisible)

    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', refreshWhenVisible)
    }
  }, [enabled])

  return now
}

const reminderTitle = (t: TFunction, scheduledAt: string, minutesLeft: number, isLate: boolean): string => {
  if (isLate)
    return t('subscriptionTools:schedule_callback.reminder_popup.title_late', { time: formatCallbackTime(scheduledAt) })

  return t('subscriptionTools:schedule_callback.reminder_popup.title_countdown', { count: minutesLeft })
}

const customerName = (reminder: CallbackReminder): string =>
  `${reminder.contact_first_name ?? ''} ${reminder.contact_last_name ?? ''}`.trim()

const useCallbackReminderPopup = (): UseCallbackReminderPopupReturn => {
  const { t } = useTranslation()
  const { reminders, dismissingIds, dismiss } = useCallbackReminders()
  const now = useNow(reminders.length > 0)

  const cards = reminders
    .filter(reminder => !isCallbackExpired(reminder.scheduled_at, now))
    .map(reminder => {
      const minutesLeft = minutesUntilCallback(reminder.scheduled_at, now)
      const isLate = !(minutesLeft > 0)

      return {
        id: reminder.id,
        title: reminderTitle(t, reminder.scheduled_at, minutesLeft, isLate),
        subtitle: t('subscriptionTools:schedule_callback.reminder_popup.scheduled_at', {
          time: formatCallbackTime(reminder.scheduled_at),
        }),
        customer: customerName(reminder),
        phone: reminder.contact_phone ?? '',
        isLate,
        hasContact: Boolean(reminder.zoho_contact_id),
        isDismissing: dismissingIds.has(reminder.id),
        closeLabel: t('subscriptionTools:schedule_callback.reminder_popup.close'),
        openContactLabel: t('subscriptionTools:schedule_callback.reminder_popup.open_contact'),
        onClose: () => {
          dismiss(reminder.id)
        },
        onOpenContact: () => {
          dismiss(reminder.id)
          window.open(`/contact/${reminder.zoho_contact_id}`, '_blank')
        },
      }
    })

  return { cards, stackLabel: t('subscriptionTools:schedule_callback.reminder_popup.stack_label') }
}

export default useCallbackReminderPopup
